import React, {FC, ReactNode} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import './ProcurementFilterPanel.css'

export interface FilterOption {
  value: string
  label: string
}

interface ProcurementFilterPanelProps {
  searchValue: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string
  referenceValue?: string
  onReferenceChange?: (value: string) => void
  vesselValue?: string
  onVesselChange?: (value: string) => void
  vesselOptions?: FilterOption[]
  departmentLabel?: string
  catalogueLabel?: string
  departmentValue?: string
  onDepartmentChange?: (value: string) => void
  departmentOptions?: FilterOption[]
  catalogueValue?: string
  onCatalogueChange?: (value: string) => void
  catalogueOptions?: FilterOption[]
  requisitionTitleValue?: string
  onRequisitionTitleChange?: (value: string) => void
  rfqTitleValue?: string
  onRfqTitleChange?: (value: string) => void
  approvalPendingWithValue?: string
  onApprovalPendingWithChange?: (value: string) => void
  approvalPendingWithOptions?: FilterOption[]
  poDateFrom?: string
  onPoDateFromChange?: (value: string) => void
  poDateTo?: string
  onPoDateToChange?: (value: string) => void
  dateFromLabel?: string
  dateToLabel?: string
  deliveredToVesselValue?: string
  onDeliveredToVesselChange?: (value: string) => void
  stageValue?: string
  onStageChange?: (value: string) => void
  stageOptions?: FilterOption[]
  additionalOpen: boolean
  onToggleAdditional: () => void
  onReset?: () => void
  extraPrimary?: ReactNode
  extraAdditional?: ReactNode
}

const labelClass = 'form-label text-muted fw-semibold fs-7 mb-2'

const defaultDeliveredOptions: FilterOption[] = [
  {value: '', label: 'All'},
  {value: 'YES', label: 'Yes'},
  {value: 'NO', label: 'No'},
]

const FilterSelect: FC<{
  label: string
  value?: string
  onChange?: (value: string) => void
  options: FilterOption[]
}> = ({label, value = '', onChange, options}) => (
  <div className='col-12 col-md-6 col-lg-3'>
    <label className={labelClass}>{label}</label>
    <SearchableSelect
      className='form-select form-select-sm'
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      style={{border: '1px solid #E4E6EF', borderRadius: '6px', padding: '8px 12px'}}
    >
      {options.map((opt) => (
        <option key={`${label}-${opt.value || 'all'}`} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </SearchableSelect>
  </div>
)

const ProcurementFilterPanel: FC<ProcurementFilterPanelProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  referenceValue,
  onReferenceChange,
  vesselValue,
  onVesselChange,
  vesselOptions = [{value: '', label: 'All'}],
  departmentValue,
  departmentLabel = 'Department',
  catalogueLabel = 'Catalogue',
  onDepartmentChange,
  departmentOptions = [{value: '', label: 'All'}],
  catalogueValue,
  onCatalogueChange,
  catalogueOptions = [{value: '', label: 'All'}],
  requisitionTitleValue,
  onRequisitionTitleChange,
  rfqTitleValue,
  onRfqTitleChange,
  approvalPendingWithValue,
  onApprovalPendingWithChange,
  approvalPendingWithOptions = [{value: '', label: 'All'}],
  poDateFrom,
  onPoDateFromChange,
  poDateTo,
  onPoDateToChange,
  dateFromLabel = 'PO Date From',
  dateToLabel = 'PO Date To',
  deliveredToVesselValue,
  onDeliveredToVesselChange,
  stageValue,
  onStageChange,
  stageOptions = [{value: '', label: 'All'}],
  additionalOpen,
  onToggleAdditional,
  onReset,
  extraPrimary,
  extraAdditional,
}) => {
  return (
    <div className='procurement-filter-panel p-4 mb-4'>
      <div className='row g-3 align-items-end'>
        <div className='col-12 col-md-6 col-lg-3'>
          <label className={labelClass}>Search / Reference</label>
          <div className='position-relative'>
            <div className='position-absolute ms-3' style={{top: '50%', transform: 'translateY(-50%)'}}>
              <KTSVG path='/media/icons/duotune/general/gen021.svg' className='svg-icon-2' />
            </div>
            <input
              type='text'
              className='form-control form-control-sm ps-10'
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        {onReferenceChange && (
          <div className='col-12 col-md-6 col-lg-3'>
            <label className={labelClass}>Req / PO / Invoice Ref</label>
            <input
              type='text'
              className='form-control form-control-sm'
              value={referenceValue ?? ''}
              onChange={(e) => onReferenceChange(e.target.value)}
              placeholder='Reference number'
            />
          </div>
        )}

        {onVesselChange && (
          <FilterSelect
            label='Vessel'
            value={vesselValue}
            onChange={onVesselChange}
            options={vesselOptions}
          />
        )}

        {onStageChange && (
          <FilterSelect label='Stage' value={stageValue} onChange={onStageChange} options={stageOptions} />
        )}

        {extraPrimary}
      </div>

      <div className='d-flex flex-wrap justify-content-between align-items-center mt-3'>
        <button type='button' className='btn btn-sm btn-light-primary' onClick={onToggleAdditional}>
          {additionalOpen ? 'Hide Additional Filters' : 'Additional Filters'}
        </button>
        {onReset && (
          <button type='button' className='btn btn-sm btn-light' onClick={onReset}>
            Reset Filters
          </button>
        )}
      </div>

      {additionalOpen && (
        <div className='row g-3 mt-1 pt-3 border-top'>
          {onDepartmentChange && (
            <FilterSelect
              label={departmentLabel}
              value={departmentValue}
              onChange={onDepartmentChange}
              options={departmentOptions}
            />
          )}

          {onCatalogueChange && (
            <FilterSelect
              label={catalogueLabel}
              value={catalogueValue}
              onChange={onCatalogueChange}
              options={catalogueOptions}
            />
          )}

          {onRequisitionTitleChange && (
            <div className='col-12 col-md-6 col-lg-3'>
              <label className={labelClass}>Requisition Title</label>
              <input
                type='text'
                className='form-control form-control-sm'
                value={requisitionTitleValue ?? ''}
                onChange={(e) => onRequisitionTitleChange(e.target.value)}
                placeholder='Title / Name'
              />
            </div>
          )}

          {onRfqTitleChange && (
            <div className='col-12 col-md-6 col-lg-3'>
              <label className={labelClass}>RFQ Title</label>
              <input
                type='text'
                className='form-control form-control-sm'
                value={rfqTitleValue ?? ''}
                onChange={(e) => onRfqTitleChange(e.target.value)}
                placeholder='RFQ title'
              />
            </div>
          )}

          {onApprovalPendingWithChange && (
            <FilterSelect
              label='Approval Pending With'
              value={approvalPendingWithValue}
              onChange={onApprovalPendingWithChange}
              options={approvalPendingWithOptions}
            />
          )}

          {onPoDateFromChange && (
            <div className='col-12 col-md-6 col-lg-3'>
              <label className={labelClass}>{dateFromLabel}</label>
              <input
                type='date'
                className='form-control form-control-sm'
                value={poDateFrom ?? ''}
                onChange={(e) => onPoDateFromChange(e.target.value)}
              />
            </div>
          )}

          {onPoDateToChange && (
            <div className='col-12 col-md-6 col-lg-3'>
              <label className={labelClass}>{dateToLabel}</label>
              <input
                type='date'
                className='form-control form-control-sm'
                value={poDateTo ?? ''}
                onChange={(e) => onPoDateToChange(e.target.value)}
              />
            </div>
          )}

          {onDeliveredToVesselChange && (
            <FilterSelect
              label='Delivered To Vessel'
              value={deliveredToVesselValue}
              onChange={onDeliveredToVesselChange}
              options={defaultDeliveredOptions}
            />
          )}

          {extraAdditional}
        </div>
      )}
    </div>
  )
}

export default ProcurementFilterPanel
